import { useContext, useState, useRef, useEffect } from "react";
import { useFormik } from "formik";
import { useNavigate, useSearchParams } from "react-router-dom";

import { tratamentoSchema } from "../../../shared/util/schemas";
import Sidebar from "../../../shared/components/sidebar/Sidebar";
import Navbar from "../../../shared/components/navbar/Navbar";
import { useHttpClient } from "../../../shared/hooks/http-hook";
import { AuthContext } from "../../../shared/context/auth-context";
import { ClinicaContext } from "../../../shared/context/clinica-context";
import { useLoading } from "../../../shared/context/LoadingContext";
import toast from "react-hot-toast";

//Mui icons
import VaccinesIcon from "@mui/icons-material/Vaccines";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

//Mui Elements
import OutlinedInput from "@mui/material/OutlinedInput";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import ListItemText from "@mui/material/ListItemText";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import InputLabel from "@mui/material/InputLabel";
import IconButton from "@mui/material/IconButton";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import "./New.scss";
import "../../../style/formError.scss";

const DENTES = [
  "11",
  "12",
  "13",
  "14",
  "15",
  "16",
  "17",
  "18",
  "21",
  "22",
  "23",
  "24",
  "25",
  "26",
  "27",
  "28",
  "31",
  "32",
  "33",
  "34",
  "35",
  "36",
  "37",
  "38",
  "41",
  "42",
  "43",
  "44",
  "45",
  "46",
  "47",
  "48",
  "51",
  "52",
  "53",
  "54",
  "55",
  "61",
  "62",
  "63",
  "64",
  "65",
  "71",
  "72",
  "73",
  "74",
  "75",
  "81",
  "82",
  "83",
  "84",
  "85",
  "Arcada Superior",
  "Arcada Inferior",
  "Arcadas",
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
      color: "#0c53fb",
    },
  },
};

function NewTratamento() {
  const { startLoading, stopLoading } = useLoading();
  const { sendRequest } = useHttpClient();
  const auth = useContext(AuthContext);
  const clinica = useContext(ClinicaContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const duplicarId = searchParams.get("duplicar");
  const aceitaDentesRef = useRef(null);

  // Estados existentes
  const [formikInitialValues, setFormikInitialValues] = useState({
    codigo: "",
    designacao: "",
    precoParticular: 0,
  });
  const [categoriasTratamento, setCategoriasTratamento] = useState([]);
  const [categoriaTratamento, setCategoriaTratamento] = useState("");
  const [qtdDentes, setQtdDentes] = useState(0);
  const [qtdFaces, setQtdFaces] = useState(5);
  const [multiDentes, setMultiDentes] = useState(false);
  const [geraLembreteLimpeza, setGeraLembreteLimpeza] = useState(false);
  const [mensagemErro, setMensagemErro] = useState("");
  const [dentesEspecificos, setDentesEspecificos] = useState([]);
  const [aceitaFaces, setAceitaFaces] = useState(true);

  //Stock
  const [produtos, setProdutos] = useState([]);
  const [consumoEstimado, setConsumoEstimado] = useState([]);

  // Novos estados para instituições
  const [instituicoes, setInstituicoes] = useState([]);
  const [precosPorInstituicao, setPrecosPorInstituicao] = useState([]);

  // Fornecedor (laboratório) opcional associado ao tratamento
  const [fornecedores, setFornecedores] = useState([]);
  const [fornecedor, setFornecedor] = useState("");

  const multiDentesChangeHandler = (valor) => {
    setMultiDentes(valor);
    if (!valor) {
      setQtdDentes(0);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      startLoading();
      try {
        // Buscar categorias
        const categoriasResponse = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/categoriastratamento/clinica/${clinica.clinica._id}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        );
        setCategoriasTratamento(categoriasResponse.categoriasTratamento);
        setCategoriaTratamento(
          categoriasResponse.categoriasTratamento.filter(
            (c) => c.categoria === "Outra",
          )[0]?.id || "",
        );

        // Buscar instituições
        const instituicoesResponse = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/instituicoes/clinica/${clinica.clinica._id}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        );
        setInstituicoes(instituicoesResponse.instituicoes || []);

        const produtosResponse = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/produtos/clinica/${clinica.clinica._id}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        );
        setProdutos(produtosResponse.produtos || []);

        // Buscar fornecedores (laboratórios) da clínica
        const fornecedoresResponse = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/fornecedores/clinica/${clinica.clinica._id}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        );
        setFornecedores(fornecedoresResponse.fornecedores || []);

        // Se estiver a duplicar, buscar o tratamento de origem e pré-preencher
        if (duplicarId) {
          const tratamentoResponse = await sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/tratamentos/${duplicarId}`,
            "GET",
            null,
            { Authorization: "Bearer " + auth.token },
          );
          processTratamentoData(tratamentoResponse.tratamento);
        }
      } catch (err) {
        console.error("err", err);
      } finally {
        stopLoading();
      }
    };
    fetchData();
  }, [auth.token, clinica.clinica._id, sendRequest, duplicarId]);

  // Pré-preenche o formulário com os dados do tratamento a duplicar
  const processTratamentoData = (tratamento) => {
    setCategoriaTratamento(tratamento.categoria);
    setAceitaFaces(tratamento.aceitaFaces);
    setMultiDentes(tratamento.multiDentes);
    /* Herdado como os outros: quem duplica uma limpeza quer outra limpeza. */
    setGeraLembreteLimpeza(!!tratamento.geraLembreteLimpeza);

    if (tratamento.dentesEspecificos) {
      setDentesEspecificos(tratamento.dentesEspecificos.split(", "));
    }

    setQtdDentes(tratamento.qtdDentes);
    setQtdFaces(tratamento.qtdFaces);

    setFornecedor(tratamento.fornecedor?._id || tratamento.fornecedor || "");

    if (
      tratamento.precosPorInstituicao &&
      tratamento.precosPorInstituicao.length > 0
    ) {
      // Remove o _id dos subdocumentos de origem para que o novo
      // tratamento gere subdocumentos independentes
      setPrecosPorInstituicao(
        tratamento.precosPorInstituicao.map(({ _id, ...resto }) => ({
          ...resto,
          instituicao: resto.instituicao?._id || resto.instituicao || "",
        })),
      );
    }

    if (tratamento.consumoEstimado && tratamento.consumoEstimado.length > 0) {
      setConsumoEstimado(
        tratamento.consumoEstimado.map(({ _id, ...grupo }) => ({
          ...grupo,
          opcoes: grupo.opcoes.map(({ _id: opcaoId, ...opcao }) => ({
            ...opcao,
            produto: opcao.produto?._id || opcao.produto || "",
            quantidade: String(opcao.quantidade),
          })),
        })),
      );
    }

    setFormikInitialValues({
      codigo: tratamento.codigo,
      designacao: tratamento.designacao,
      precoParticular: tratamento.preco_particular,
    });

    setTimeout(() => {
      if (aceitaDentesRef.current) {
        aceitaDentesRef.current.checked = tratamento.aceitaDentes;
      }
    }, 100);
  };

  const adicionarInstituicao = () => {
    setPrecosPorInstituicao([
      ...precosPorInstituicao,
      {
        instituicao: "",
        preco_segurado: 0,
        preco_beneficiario: 0,
        comparticipacao: 0,
        coberto: true,
      },
    ]);
  };

  const removerInstituicao = (index) => {
    const novosPrecos = precosPorInstituicao.filter((_, i) => i !== index);
    setPrecosPorInstituicao(novosPrecos);
  };

  const atualizarPrecoInstituicao = (index, campo, valor) => {
    const novosPrecos = [...precosPorInstituicao];
    novosPrecos[index] = {
      ...novosPrecos[index],
      [campo]: valor,
    };
    setPrecosPorInstituicao(novosPrecos);
  };

  const handleCancel = () => {
    resetForm();
    setPrecosPorInstituicao([]);
    setFornecedor("");
    navigate("/tratamentos");
  };

  const validarDados = (values) => {
    let erro = false;
    let mensagem = "";

    if (multiDentes && qtdDentes <= 0) {
      mensagem =
        "Tratamentos multiDentes requerem um número de dentes superior a zero!";
      erro = true;
    } else if (aceitaFaces && qtdFaces <= 0) {
      mensagem =
        "Para tratamentos que aceitam faces, a quantidade de faces deve ter um valor superior a zero!";
      erro = true;
      // }
      //  else if (values.precoParticular <= 0) {
      //   mensagem = "Preço particular deve ter um valor superior a zero!";
      //   erro = true;
    } else {
      // Validar preços por instituição
      for (let i = 0; i < precosPorInstituicao.length; i++) {
        const preco = precosPorInstituicao[i];
        if (!preco.instituicao) {
          mensagem = `Instituição ${i + 1}: Selecione uma instituição.`;
          erro = true;
          break;
        }
        if (
          preco.coberto &&
          (preco.preco_segurado <= 0 ||
            preco.preco_beneficiario <= 0 ||
            preco.comparticipacao <= 0)
        ) {
          mensagem = `Instituição ${
            i + 1
          }: Para tratamentos cobertos, todos os campos de preço devem ter valor superior a zero!`;
          erro = true;
          break;
        }

        // Verificar se soma está correta
        if (
          preco.coberto &&
          preco.preco_beneficiario + preco.comparticipacao !==
            preco.preco_segurado
        ) {
          mensagem = `Instituição ${
            i + 1
          }: Preço Beneficiário + Comparticipação deve ser igual ao Preço Segurado!`;
          erro = true;
          break;
        }
      }

      // Verificar instituições duplicadas
      const instituicoesUsadas = precosPorInstituicao.map((p) => p.instituicao);
      const instituicoesDuplicadas = instituicoesUsadas.filter(
        (inst, index) =>
          instituicoesUsadas.indexOf(inst) !== index && inst !== "",
      );
      if (instituicoesDuplicadas.length > 0) {
        mensagem =
          "Não é possível ter a mesma instituição configurada múltiplas vezes.";
        erro = true;
      }
    }
    setMensagemErro(mensagem);
    return !erro;
  };

  const onSubmit = async (values, actions) => {
    if (!validarDados(values)) return;

    startLoading();
    try {
      const formData = new FormData();
      formData.append("codigo", values.codigo);
      formData.append("designacao", values.designacao);
      formData.append("categoria", categoriaTratamento);
      formData.append("preco_particular", values.precoParticular);
      formData.append("aceitaFaces", aceitaFaces);
      formData.append("aceitaDentes", aceitaDentesRef.current.checked);
      formData.append("multiDentes", multiDentes);
      formData.append("geraLembreteLimpeza", geraLembreteLimpeza);
      formData.append("qtdDentes", qtdDentes);
      formData.append("qtdFaces", aceitaFaces ? qtdFaces : 5);
      formData.append("ativo", true);
      formData.append("clinica", clinica.clinica._id);
      formData.append(
        "dentesEspecificos",
        dentesEspecificos.length > 0 ? dentesEspecificos.join(", ") : "",
      );
      formData.append("criadoPor", auth.userId);
      formData.append("fornecedor", fornecedor || "");

      // Adicionar preços por instituição
      formData.append(
        "precosPorInstituicao",
        JSON.stringify(precosPorInstituicao),
      );

      //produtos stock
      const consumoEstimadoParsed = consumoEstimado.map((grupo) => ({
        ...grupo,
        opcoes: grupo.opcoes.map((opcao) => ({
          ...opcao,
          quantidade: parseFloat(opcao.quantidade) || 0,
        })),
      }));
      formData.append("consumoEstimado", JSON.stringify(consumoEstimadoParsed));

      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/tratamentos`,
        "POST",
        formData,
        { Authorization: "Bearer " + auth.token },
      );

      toast.success("Tratamento adicionado com sucesso!");
      handleCancel();
    } catch (err) {
      console.error("err", err);
    } finally {
      stopLoading();
    }
  };

  const dentesEspecificosChangeHandler = (event) => {
    const {
      target: { value },
    } = event;
    setDentesEspecificos(typeof value === "string" ? value.split(",") : value);
  };

  const {
    values,
    touched,
    handleBlur,
    handleChange,
    errors,
    handleSubmit,
    isSubmitting,
    resetForm,
  } = useFormik({
    initialValues: formikInitialValues,
    enableReinitialize: true,
    validationSchema: tratamentoSchema,
    onSubmit,
  });

  const getInstituicaoNome = (instituicaoId) => {
    const inst = instituicoes.find((i) => i._id === instituicaoId);
    return inst ? inst.nome : "";
  };

  return (
    <>
      <div className="new">
        <Sidebar />
        <div className="newContainer">
          <Navbar
            title="Adicionar Tratamento"
            icon={VaccinesIcon}
            paths={[
              { nome: "Tratamentos", link: `../../tratamentos` },
              { nome: "Novo" },
            ]}
          />
          <div className="bottom caixa">
            <div className="bottom-left">
              <img src="/images/tratamentos.jpg" alt="" />
            </div>
            <div className="bottom-right">
              {/* Campos básicos */}
              <div className="form-input span2">
                <input
                  id="codigo"
                  name="codigo"
                  type="text"
                  placeholder="codigo"
                  value={values.codigo}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    errors.codigo && touched.codigo
                      ? "input-error input"
                      : "input"
                  }
                />
                <label className="label" htmlFor="codigo">
                  Código
                </label>
                {errors.codigo && touched.codigo ? (
                  <p className="error-message">{errors.codigo}</p>
                ) : null}
              </div>

              <div className="form-input span2">
                <input
                  id="designacao"
                  name="designacao"
                  type="text"
                  placeholder="Designação"
                  value={values.designacao}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    errors.designacao && touched.designacao
                      ? "input-error input"
                      : "input"
                  }
                />
                <label className="label" htmlFor="designacao">
                  Designação*
                </label>
                {errors.designacao && touched.designacao ? (
                  <p className="error-message">{errors.designacao}</p>
                ) : null}
              </div>

              {categoriaTratamento && (
                <div className="customInputContainer span2">
                  <select
                    className="customInputSelect"
                    id="categoria"
                    name="categoria"
                    value={categoriaTratamento}
                    onChange={(e) => setCategoriaTratamento(e.target.value)}
                  >
                    {categoriasTratamento &&
                      categoriasTratamento.map((cat, index) => (
                        <option value={cat.id} key={index}>
                          {cat.categoria}
                        </option>
                      ))}
                  </select>
                  <label className="customInputLabelSelect">Categoria</label>
                </div>
              )}

              {/* Fornecedor (laboratório) opcional */}
              <div className="customInputContainer span2">
                <select
                  className="customInputSelect"
                  id="fornecedor"
                  name="fornecedor"
                  value={fornecedor}
                  onChange={(e) => setFornecedor(e.target.value)}
                >
                  <option value="">Sem fornecedor</option>
                  {fornecedores.map((f) => (
                    <option value={f._id} key={f._id}>
                      {f.nome}
                    </option>
                  ))}
                </select>
                <label className="customInputLabelSelect">
                  Fornecedor (opcional)
                </label>
              </div>

              {/* Preço particular */}
              <div className="form-input span2">
                <input
                  id="precoParticular"
                  name="precoParticular"
                  type="number"
                  placeholder="Preço Particular"
                  value={values.precoParticular}
                  onChange={handleChange}
                  className="input"
                />
                <label className="label" htmlFor="precoParticular">
                  Preço Particular*
                </label>
              </div>

              {/* Campos de quantidade */}
              {multiDentes && (
                <div className="form-input span2">
                  <input
                    id="qtdDentes"
                    name="qtdDentes"
                    type="number"
                    placeholder="Qtd Dentes"
                    value={qtdDentes}
                    onChange={(e) => setQtdDentes(e.target.value)}
                  />
                  <label className="label" htmlFor="qtdDentes">
                    Qtd Dentes*
                  </label>
                </div>
              )}

              {aceitaFaces && (
                <div className="form-input span2">
                  <input
                    id="qtdFaces"
                    name="qtdFaces"
                    type="number"
                    placeholder="Qtd Faces"
                    value={qtdFaces}
                    onChange={(e) => setQtdFaces(e.target.value)}
                  />
                  <label className="label" htmlFor="qtdFaces">
                    Qtd Faces*
                  </label>
                </div>
              )}

              {/* Dentes específicos */}
              <FormControl className="span2">
                <InputLabel id="demo-multiple-checkbox-label">
                  Dentes Específicos
                </InputLabel>
                <Select
                  id="demo-multiple-checkbox"
                  multiple
                  value={dentesEspecificos}
                  onChange={dentesEspecificosChangeHandler}
                  input={<OutlinedInput label="Dentes" />}
                  renderValue={(selected) => selected.join(", ")}
                  MenuProps={MenuProps}
                  style={{ color: "#0c53fb" }}
                >
                  {DENTES.map((dente) => (
                    <MenuItem key={dente} value={dente}>
                      <Checkbox
                        checked={dentesEspecificos.indexOf(dente) > -1}
                      />
                      <ListItemText primary={dente} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Checkboxes */}
              <div className="tratamentos-checkboxes__container">
                <div className="form-input">
                  <div className="checkbox-tratamento">
                    <span className="checkbox-text">Aceita Faces?</span>
                    <input
                      type="checkbox"
                      id="aceitaFaces"
                      checked={aceitaFaces}
                      onChange={(e) => setAceitaFaces(e.target.checked)}
                    />
                    <label
                      htmlFor="aceitaFaces"
                      className="toggle tratamento__checkbox-label"
                    >
                      <span>
                        <svg width="10px" height="10px" viewBox="0 0 10 10">
                          <path d="M5,1 L5,1 C2.790861,1 1,2.790861 1,5 L1,5 C1,7.209139 2.790861,9 5,9 L5,9 C7.209139,9 9,7.209139 9,5 L9,5 C9,2.790861 7.209139,1 5,1 L5,9 L5,1 Z"></path>
                        </svg>
                      </span>
                    </label>
                  </div>
                </div>

                <div className="form-input">
                  <div className="checkbox-tratamento">
                    <span className="checkbox-text">Aceita Dentes?</span>
                    <input
                      type="checkbox"
                      id="aceitaDentes"
                      ref={aceitaDentesRef}
                      defaultChecked={true}
                    />
                    <label
                      htmlFor="aceitaDentes"
                      className="toggle tratamento__checkbox-label"
                    >
                      <span>
                        <svg width="10px" height="10px" viewBox="0 0 10 10">
                          <path d="M5,1 L5,1 C2.790861,1 1,2.790861 1,5 L1,5 C1,7.209139 2.790861,9 5,9 L5,9 C7.209139,9 9,7.209139 9,5 L9,5 C9,2.790861 7.209139,1 5,1 L5,9 L5,1 Z"></path>
                        </svg>
                      </span>
                    </label>
                  </div>
                </div>

                <div className="form-input">
                  <div className="checkbox-tratamento">
                    <span className="checkbox-text">Múltiplos Dentes?</span>
                    <input
                      type="checkbox"
                      id="multiDentes"
                      checked={multiDentes}
                      onChange={(e) =>
                        multiDentesChangeHandler(e.target.checked)
                      }
                    />
                    <label
                      htmlFor="multiDentes"
                      className="toggle tratamento__checkbox-label"
                    >
                      <span>
                        <svg width="10px" height="10px" viewBox="0 0 10 10">
                          <path d="M5,1 L5,1 C2.790861,1 1,2.790861 1,5 L1,5 C1,7.209139 2.790861,9 5,9 L5,9 C7.209139,9 9,7.209139 9,5 L9,5 C9,2.790861 7.209139,1 5,1 L5,9 L5,1 Z"></path>
                        </svg>
                      </span>
                    </label>
                  </div>
                </div>

                {/* Marcar aqui, e não por categoria: a destartarização vive em
                    Periodontia na maioria das clínicas, e uma categoria de
                    Prevenção tem a limpeza e tem o selante — que não pede
                    revisão daqui a seis meses. */}
                <div className="form-input">
                  <div className="checkbox-tratamento">
                    <span className="checkbox-text">
                      Pede próxima limpeza?
                    </span>
                    <input
                      type="checkbox"
                      id="geraLembreteLimpeza"
                      checked={geraLembreteLimpeza}
                      onChange={(e) =>
                        setGeraLembreteLimpeza(e.target.checked)
                      }
                    />
                    <label
                      htmlFor="geraLembreteLimpeza"
                      className="toggle tratamento__checkbox-label"
                    >
                      <span>
                        <svg width="10px" height="10px" viewBox="0 0 10 10">
                          <path d="M5,1 L5,1 C2.790861,1 1,2.790861 1,5 L1,5 C1,7.209139 2.790861,9 5,9 L5,9 C7.209139,9 9,7.209139 9,5 L9,5 C9,2.790861 7.209139,1 5,1 L5,9 L5,1 Z"></path>
                        </svg>
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Seção de Instituições */}
              <div style={{ marginTop: "20px", gridColumn: "1/-1" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                  }}
                >
                  <h3>Coberturas por Instituição</h3>
                  <button
                    type="button"
                    onClick={adicionarInstituicao}
                    className="blue-button"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      padding: "8px 16px",
                    }}
                    disabled={instituicoes.length === 0}
                  >
                    <AddIcon fontSize="small" />
                    Adicionar Instituição
                  </button>
                </div>

                {instituicoes.length === 0 && (
                  <p style={{ color: "#666", fontStyle: "italic" }}>
                    Nenhuma instituição configurada. Configure instituições
                    primeiro para adicionar coberturas.
                  </p>
                )}

                {precosPorInstituicao.map((precoInst, index) => (
                  <Accordion key={index} style={{ marginBottom: "10px" }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <span>
                          {precoInst.instituicao
                            ? getInstituicaoNome(precoInst.instituicao)
                            : `Instituição ${index + 1}`}
                          {!precoInst.coberto && " (Não Coberto)"}
                        </span>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            removerInstituicao(index);
                          }}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </AccordionSummary>
                    <AccordionDetails>
                      <div
                        className="grid-instituicao"
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr 1fr",
                          gap: "15px",
                        }}
                      >
                        {/* Seleção da instituição */}
                        <div
                          className="customInputContainer"
                          style={{ gridColumn: "1/3" }}
                        >
                          <select
                            className="customInputSelect"
                            value={precoInst.instituicao}
                            onChange={(e) =>
                              atualizarPrecoInstituicao(
                                index,
                                "instituicao",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">Selecione uma instituição</option>
                            {instituicoes
                              .filter(
                                (inst) =>
                                  !precosPorInstituicao.some(
                                    (p, i) =>
                                      i !== index && p.instituicao === inst._id,
                                  ),
                              )
                              .map((inst) => (
                                <option value={inst._id} key={inst._id}>
                                  {inst.nome}
                                </option>
                              ))}
                          </select>
                          <label className="customInputLabelSelect">
                            Instituição
                          </label>
                        </div>

                        {/* Checkbox coberto */}
                        <div
                          className="form-input"
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <div
                            className="checkbox-tratamento"
                            style={{ flexDirection: "row" }}
                          >
                            <span className="checkbox-text">Coberto?</span>
                            <input
                              type="checkbox"
                              id={`coberto-${index}`}
                              checked={precoInst.coberto}
                              onChange={(e) =>
                                atualizarPrecoInstituicao(
                                  index,
                                  "coberto",
                                  e.target.checked,
                                )
                              }
                            />
                            <label
                              htmlFor={`coberto-${index}`}
                              className="toggle tratamento__checkbox-label"
                            >
                              <span>
                                <svg
                                  width="10px"
                                  height="10px"
                                  viewBox="0 0 10 10"
                                >
                                  <path d="M5,1 L5,1 C2.790861,1 1,2.790861 1,5 L1,5 C1,7.209139 2.790861,9 5,9 L5,9 C7.209139,9 9,7.209139 9,5 L9,5 C9,2.790861 7.209139,1 5,1 L5,9 L5,1 Z"></path>
                                </svg>
                              </span>
                            </label>
                          </div>
                        </div>

                        {precoInst.coberto && (
                          <>
                            {/* Preço segurado */}
                            <div className="form-input">
                              <input
                                type="number"
                                placeholder="Preço Segurado"
                                value={precoInst.preco_segurado}
                                onChange={(e) =>
                                  atualizarPrecoInstituicao(
                                    index,
                                    "preco_segurado",
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                className="input"
                              />
                              <label className="label">Preço Segurado*</label>
                            </div>

                            {/* Comparticipação */}
                            <div className="form-input">
                              <input
                                type="number"
                                placeholder="Comparticipação"
                                value={precoInst.comparticipacao}
                                onChange={(e) =>
                                  atualizarPrecoInstituicao(
                                    index,
                                    "comparticipacao",
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                className="input"
                              />
                              <label className="label">Comparticipação*</label>
                            </div>

                            {/* Preço beneficiário */}
                            <div className="form-input">
                              <input
                                type="number"
                                placeholder="Preço Beneficiário"
                                value={precoInst.preco_beneficiario}
                                onChange={(e) =>
                                  atualizarPrecoInstituicao(
                                    index,
                                    "preco_beneficiario",
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                className="input"
                              />
                              <label className="label">
                                Preço Beneficiário*
                              </label>
                            </div>

                            {/* Validação visual */}
                            <div style={{ gridColumn: "1 / -1" }}>
                              {precoInst.preco_segurado > 0 &&
                                precoInst.comparticipacao > 0 &&
                                precoInst.preco_beneficiario > 0 &&
                                precoInst.preco_beneficiario +
                                  precoInst.comparticipacao !==
                                  precoInst.preco_segurado && (
                                  <p style={{ color: "red", fontSize: "12px" }}>
                                    ⚠️ Preço Beneficiário + Comparticipação deve
                                    igual Preço Segurado
                                  </p>
                                )}
                            </div>
                          </>
                        )}
                      </div>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </div>

              <div style={{ marginTop: "20px", gridColumn: "1/-1" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                  }}
                >
                  <h3>Consumo Estimado de Stock</h3>
                  <button
                    type="button"
                    className="blue-button"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      padding: "8px 16px",
                    }}
                    onClick={() =>
                      setConsumoEstimado([
                        ...consumoEstimado,
                        { opcoes: [{ produto: "", quantidade: 1 }] },
                      ])
                    }
                  >
                    <AddIcon fontSize="small" />
                    Adicionar Produto
                  </button>
                </div>

                {consumoEstimado.map((grupo, grupoIndex) => (
                  <Accordion key={grupoIndex} style={{ marginBottom: "10px" }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <span>
                          {grupo.opcoes[0]?.produto
                            ? `Produto: ${produtos.find((p) => p._id === grupo.opcoes[0].produto)?.nome || "..."}`
                            : `Grupo ${grupoIndex + 1}`}
                        </span>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setConsumoEstimado(
                              consumoEstimado.filter(
                                (_, i) => i !== grupoIndex,
                              ),
                            );
                          }}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </AccordionSummary>
                    <AccordionDetails>
                      {grupo.opcoes.map((opcao, opcaoIndex) => (
                        <div
                          key={opcaoIndex}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "2fr 1fr auto",
                            gap: "10px",
                            alignItems: "center",
                            marginBottom: "10px",
                          }}
                        >
                          <div className="customInputContainer">
                            <select
                              className="customInputSelect"
                              value={opcao.produto}
                              onChange={(e) => {
                                const novo = [...consumoEstimado];
                                novo[grupoIndex].opcoes[opcaoIndex].produto =
                                  e.target.value;
                                setConsumoEstimado(novo);
                              }}
                            >
                              <option value="">
                                {opcaoIndex === 0
                                  ? "Produto principal"
                                  : `Substituto ${opcaoIndex}`}
                              </option>
                              {produtos.map((p) => (
                                <option key={p._id} value={p._id}>
                                  {p.nome}
                                </option>
                              ))}
                            </select>
                            <label className="customInputLabelSelect">
                              {opcaoIndex === 0
                                ? "Produto Principal"
                                : `Substituto ${opcaoIndex}`}
                            </label>
                          </div>
                          <div className="form-input">
                            <input
                              type="number"
                              className="input"
                              value={opcao.quantidade}
                              min={1}
                              onChange={(e) => {
                                const novo = [...consumoEstimado];
                                novo[grupoIndex].opcoes[opcaoIndex].quantidade =
                                  parseFloat(e.target.value) || 0;
                                setConsumoEstimado(novo);
                              }}
                            />
                            <label className="label">Quantidade</label>
                          </div>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={
                              grupo.opcoes.length === 1 && opcaoIndex === 0
                            }
                            onClick={() => {
                              const novo = [...consumoEstimado];
                              novo[grupoIndex].opcoes = novo[
                                grupoIndex
                              ].opcoes.filter((_, i) => i !== opcaoIndex);
                              setConsumoEstimado(novo);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="blue-button"
                        style={{
                          marginTop: "5px",
                          padding: "5px 12px",
                          fontSize: "12px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                        onClick={() => {
                          const novo = [...consumoEstimado];
                          novo[grupoIndex].opcoes.push({
                            produto: "",
                            quantidade: 0,
                          });
                          setConsumoEstimado(novo);
                        }}
                      >
                        <AddIcon fontSize="small" />
                        Adicionar Substituto
                      </button>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </div>

              {mensagemErro && (
                <div className="fluxo-atendimento__modal__erros">
                  {mensagemErro}
                </div>
              )}

              <div className="botoes">
                <button className="cancel-btn" onClick={handleCancel}>
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="blue-button"
                  onClick={handleSubmit}
                >
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default NewTratamento;
